import { useState, useEffect, useCallback, type ComponentType } from 'react'
import {
  SlidersHorizontal,
  RotateCcw,
  Type,
  Heading,
  SunMoon,
  Sun,
  Moon,
  Clock3,
  Minus,
  Plus,
  Rows3,
  Sparkles,
  Check,
  Palette,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/store'
import type { Theme } from '@/types'
import {
  applyResolvedAppearance, clearAllColourPreferences, DEFAULT_WORKSPACE_COLOUR, DEFAULT_WORKSPACE_SHADE,
  readModeTextColour, readModeWorkspaceColour, readModeWorkspaceShade,
  writeModeTextColour, writeModeWorkspaceColour, writeModeWorkspaceShade, workspaceShadeColour,
} from '@/lib/appearancePreferences'

const TEXT_KEY = 'opsbridge-text-scale'
const HEADING_KEY = 'opsbridge-heading-scale'
const DENSITY_KEY = 'opsbridge-layout-density'
const DEFAULT_SCALE = 1
const DEFAULT_DENSITY: Density = 'comfortable'

type Density = 'compact' | 'comfortable' | 'spacious'

type ColourStop = { at: number; hex: string }

const COLOUR_STOPS: ColourStop[] = [
  { at: 0, hex: '#020617' },
  { at: 14, hex: '#334155' },
  { at: 28, hex: '#2563EB' },
  { at: 40, hex: '#0891B2' },
  { at: 52, hex: '#059669' },
  { at: 64, hex: '#CA8A04' },
  { at: 75, hex: '#DC2626' },
  { at: 87, hex: '#9333EA' },
  { at: 100, hex: '#F8FAFC' },
]

const COLOUR_GRADIENT = `linear-gradient(90deg, ${COLOUR_STOPS.map(stop => `${stop.hex} ${stop.at}%`).join(', ')})`

function hexToRgb(hex: string) {
  const clean = hex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null
  return { r: parseInt(clean.slice(0, 2), 16), g: parseInt(clean.slice(2, 4), 16), b: parseInt(clean.slice(4, 6), 16) }
}

function rgbToHex(value: string, fallback: string) {
  const hex = value.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex.toUpperCase()
  const match = hex.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (!match) return fallback
  return '#' + [match[1], match[2], match[3]].map(v => Math.max(0, Math.min(255, Number(v))).toString(16).padStart(2, '0')).join('').toUpperCase()
}

function colourAt(position: number) {
  const pos = Math.max(0, Math.min(100, position))
  let left = COLOUR_STOPS[0], right = COLOUR_STOPS[COLOUR_STOPS.length - 1]
  for (let i = 0; i < COLOUR_STOPS.length - 1; i++) {
    if (pos >= COLOUR_STOPS[i].at && pos <= COLOUR_STOPS[i + 1].at) { left = COLOUR_STOPS[i]; right = COLOUR_STOPS[i + 1]; break }
  }
  const a = hexToRgb(left.hex)!, b = hexToRgb(right.hex)!
  const t = right.at === left.at ? 0 : (pos - left.at) / (right.at - left.at)
  const mix = (x: number, y: number) => Math.round(x + (y - x) * t).toString(16).padStart(2, '0')
  return `#${mix(a.r,b.r)}${mix(a.g,b.g)}${mix(a.b,b.b)}`.toUpperCase()
}

function nearestColourPosition(hex: string) {
  const target = hexToRgb(hex)
  if (!target) return 14
  let best = 0, bestDistance = Infinity
  for (let pos = 0; pos <= 100; pos++) {
    const rgb = hexToRgb(colourAt(pos))!
    const distance = (rgb.r-target.r)**2 + (rgb.g-target.g)**2 + (rgb.b-target.b)**2
    if (distance < bestDistance) { bestDistance = distance; best = pos }
  }
  return best
}


function readStoredScale(key: string): number {
  try {
    const raw = localStorage.getItem(key)
    const parsed = raw ? parseFloat(raw) : DEFAULT_SCALE
    return Number.isFinite(parsed) ? parsed : DEFAULT_SCALE
  } catch {
    return DEFAULT_SCALE
  }
}

function readStoredDensity(): Density {
  try {
    const saved = localStorage.getItem(DENSITY_KEY)
    return saved === 'compact' || saved === 'spacious' ? saved : DEFAULT_DENSITY
  } catch {
    return DEFAULT_DENSITY
  }
}

function applyAppearance(textScale: number, headingScale: number, density: Density) {
  const root = document.documentElement
  root.style.setProperty('--text-scale', String(textScale))
  root.style.setProperty('--heading-scale', String(headingScale))
  root.dataset.density = density
}

function ThemeChoice({
  value,
  active,
  icon: Icon,
  label,
  description,
  onSelect,
}: {
  value: Theme
  active: boolean
  icon: ComponentType<{ className?: string }>
  label: string
  description: string
  onSelect: (theme: Theme) => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onSelect(value)}
      className={cn(
        'group relative min-h-[88px] rounded-2xl border p-3 text-left transition-all cursor-pointer',
        active
          ? 'border-accent bg-accent/10 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-accent)_22%,transparent)]'
          : 'border-border bg-surface hover:border-accent/40 hover:bg-surface-elevated/70'
      )}
    >
      <span className={cn(
        'mb-2 flex h-8 w-8 items-center justify-center rounded-xl',
        active ? 'bg-accent text-white' : 'bg-surface-elevated text-text-muted group-hover:text-accent'
      )}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="block text-sm font-semibold text-text-primary">{label}</span>
      <span className="mt-0.5 block text-[11px] leading-snug text-text-muted">{description}</span>
      {active && (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-white">
          <Check className="h-3 w-3" />
        </span>
      )}
    </button>
  )
}

function ScaleControl({
  icon: Icon,
  label,
  description,
  value,
  min,
  max,
  onChange,
}: {
  icon: ComponentType<{ className?: string }>
  label: string
  description: string
  value: number
  min: number
  max: number
  onChange: (value: number) => void
}) {
  const step = 0.05
  const changeBy = (amount: number) => {
    const next = Math.max(min, Math.min(max, Number((value + amount).toFixed(2))))
    onChange(next)
  }

  return (
    <div className="p-4 sm:p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-text-primary">{label}</div>
            <p className="mt-0.5 text-xs leading-relaxed text-text-muted">{description}</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1 rounded-xl border border-border bg-surface-elevated p-1">
          <button
            type="button"
            aria-label={`Decrease ${label.toLowerCase()}`}
            disabled={value <= min}
            onClick={() => changeBy(-step)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30 cursor-pointer"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-12 text-center font-mono text-xs font-semibold tabular-nums text-accent">
            {Math.round(value * 100)}%
          </span>
          <button
            type="button"
            aria-label={`Increase ${label.toLowerCase()}`}
            disabled={value >= max}
            onClick={() => changeBy(step)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30 cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(parseFloat(event.target.value))}
        className="w-full accent-blue-500 cursor-pointer"
        aria-label={label}
      />
      <div className="mt-1 flex justify-between text-[10px] text-text-muted/70">
        <span>{label === 'Text size' ? 'Smaller' : 'Subtle'}</span>
        <span>{label === 'Text size' ? 'Larger' : 'Prominent'}</span>
      </div>
    </div>
  )
}

function ColourControl({ label, description, value, onChange, preview = 'text', shade = DEFAULT_WORKSPACE_SHADE, onShadeChange }: { label: string; description: string; value: string; onChange: (value: string) => void; preview?: 'text' | 'theme'; shade?: number; onShadeChange?: (value: number) => void }) {
  const position = nearestColourPosition(value)
  const deepShade = workspaceShadeColour(value, 0)
  const selectedShade = workspaceShadeColour(value, shade)
  const lightShade = workspaceShadeColour(value, 100)
  return (
    <div className="p-4 sm:p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent"><Palette className="h-4 w-4" /></span>
          <div className="min-w-0"><div className="text-sm font-semibold text-text-primary">{label}</div><p className="mt-0.5 text-xs leading-relaxed text-text-muted">{description}</p></div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-mono text-[10px] text-text-muted">{value.toUpperCase()}</span>
          <label className="relative h-9 w-9 overflow-hidden rounded-xl ring-1 ring-border shadow-sm cursor-pointer" style={{ backgroundColor: value }} title={`Choose exact ${label.toLowerCase()} colour`}>
            <input type="color" value={value} onChange={e => onChange(e.target.value.toUpperCase())} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label={`Exact ${label} colour`} />
          </label>
        </div>
      </div>
      <input type="range" min={0} max={100} step={1} value={position} onChange={e => onChange(colourAt(Number(e.target.value)))} className="ops-colour-slider w-full cursor-pointer" style={{ background: COLOUR_GRADIENT }} aria-label={`${label} colour spectrum`} />
      <div className="mt-2 flex items-center justify-between text-[10px] text-text-muted/75"><span>Darker</span><span>Full colour spectrum</span><span>Lighter</span></div>
      {preview === 'text' ? (
        <div className="mt-3 rounded-xl bg-surface-elevated/70 px-3 py-2 text-sm" style={{ color: value }}>Aa — The quick brown fox</div>
      ) : (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between gap-3 text-[10px] text-text-muted">
            <span>Theme shade</span>
            <span className="font-mono font-semibold text-text-secondary">{Math.round(shade)}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={shade}
            onChange={event => onShadeChange?.(Number(event.target.value))}
            className="ops-theme-shade-slider w-full cursor-pointer"
            style={{ background: `linear-gradient(90deg, ${deepShade} 0%, ${value} 50%, ${lightShade} 100%)` }}
            aria-label={`${label} shade`}
          />
          <div className="mt-2 flex items-center justify-between text-[10px] text-text-muted/75"><span>Deeper</span><span>Base colour</span><span>Lighter</span></div>
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-surface-elevated/65 px-3 py-2">
            <span className="h-5 w-5 rounded-md ring-1 ring-border/50" style={{ backgroundColor: selectedShade }} />
            <span className="font-mono text-[10px] text-text-muted">Applied shade {selectedShade}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export function AppearancePanel() {
  const { theme, setTheme, resolvedTheme } = useAppStore()
  const [textScale, setTextScale] = useState(DEFAULT_SCALE)
  const [headingScale, setHeadingScale] = useState(DEFAULT_SCALE)
  const [density, setDensity] = useState<Density>(DEFAULT_DENSITY)
  const [primaryColor, setPrimaryColor] = useState('#0F172A')
  const [mutedColor, setMutedColor] = useState('#56657A')
  const [workspaceColor, setWorkspaceColor] = useState(DEFAULT_WORKSPACE_COLOUR.light)
  const [workspaceShade, setWorkspaceShade] = useState(DEFAULT_WORKSPACE_SHADE)
  const [loaded, setLoaded] = useState(false)
  const [saved, setSaved] = useState(false)

  const showSaved = useCallback(() => {
    setSaved(true)
    window.setTimeout(() => setSaved(false), 1400)
  }, [])

  useEffect(() => {
    const storedText = readStoredScale(TEXT_KEY)
    const storedHeading = readStoredScale(HEADING_KEY)
    const storedDensity = readStoredDensity()
    setTextScale(storedText)
    setHeadingScale(storedHeading)
    setDensity(storedDensity)
    applyAppearance(storedText, storedHeading, storedDensity)
    setLoaded(true)
  }, [])

  const updateText = useCallback((value: number) => {
    setTextScale(value)
    applyAppearance(value, headingScale, density)
    try { localStorage.setItem(TEXT_KEY, String(value)) } catch { /* device storage unavailable */ }
    showSaved()
  }, [headingScale, density, showSaved])

  const updateHeading = useCallback((value: number) => {
    setHeadingScale(value)
    applyAppearance(textScale, value, density)
    try { localStorage.setItem(HEADING_KEY, String(value)) } catch { /* device storage unavailable */ }
    showSaved()
  }, [textScale, density, showSaved])

  const updateDensity = useCallback((value: Density) => {
    setDensity(value)
    applyAppearance(textScale, headingScale, value)
    try { localStorage.setItem(DENSITY_KEY, value) } catch { /* device storage unavailable */ }
    showSaved()
  }, [textScale, headingScale, showSaved])

  const updatePrimaryColor = useCallback((value: string) => {
    const next = value.toUpperCase()
    setPrimaryColor(next)
    writeModeTextColour(resolvedTheme, 'primary', next)
    applyResolvedAppearance(resolvedTheme)
    showSaved()
  }, [resolvedTheme, showSaved])

  const updateMutedColor = useCallback((value: string) => {
    const next = value.toUpperCase()
    setMutedColor(next)
    writeModeTextColour(resolvedTheme, 'muted', next)
    applyResolvedAppearance(resolvedTheme)
    showSaved()
  }, [resolvedTheme, showSaved])

  const updateWorkspaceColor = useCallback((value: string) => {
    const next = value.toUpperCase()
    setWorkspaceColor(next)
    writeModeWorkspaceColour(resolvedTheme, next)
    applyResolvedAppearance(resolvedTheme)
    showSaved()
  }, [resolvedTheme, showSaved])

  const updateWorkspaceShade = useCallback((value: number) => {
    const next = Math.max(0, Math.min(100, Math.round(value)))
    setWorkspaceShade(next)
    writeModeWorkspaceShade(resolvedTheme, next)
    applyResolvedAppearance(resolvedTheme)
    showSaved()
  }, [resolvedTheme, showSaved])

  const updateTheme = useCallback((value: Theme) => {
    setTheme(value)
    showSaved()
  }, [setTheme, showSaved])

  useEffect(() => {
    if (!loaded) return
    applyResolvedAppearance(resolvedTheme)
    const primary = readModeTextColour(resolvedTheme, 'primary')
    const muted = readModeTextColour(resolvedTheme, 'muted')
    const workspace = readModeWorkspaceColour(resolvedTheme)
    const shade = readModeWorkspaceShade(resolvedTheme)
    window.requestAnimationFrame(() => {
      const styles = getComputedStyle(document.documentElement)
      setPrimaryColor(primary || rgbToHex(styles.getPropertyValue('--color-text-primary'), resolvedTheme === 'dark' ? '#FFFFFF' : '#0F172A'))
      setMutedColor(muted || rgbToHex(styles.getPropertyValue('--color-text-muted'), resolvedTheme === 'dark' ? '#CBD5E1' : '#56657A'))
      setWorkspaceColor(workspace || DEFAULT_WORKSPACE_COLOUR[resolvedTheme])
      setWorkspaceShade(shade ?? DEFAULT_WORKSPACE_SHADE)
    })
  }, [resolvedTheme, loaded])

  const reset = useCallback(() => {
    setTextScale(DEFAULT_SCALE)
    setHeadingScale(DEFAULT_SCALE)
    setDensity(DEFAULT_DENSITY)
    setWorkspaceShade(DEFAULT_WORKSPACE_SHADE)
    clearAllColourPreferences()
    setTheme('auto')
    applyAppearance(DEFAULT_SCALE, DEFAULT_SCALE, DEFAULT_DENSITY)
    try {
      localStorage.setItem(TEXT_KEY, String(DEFAULT_SCALE))
      localStorage.setItem(HEADING_KEY, String(DEFAULT_SCALE))
      localStorage.setItem(DENSITY_KEY, DEFAULT_DENSITY)
    } catch { /* device storage unavailable */ }
    applyResolvedAppearance(resolvedTheme)
    window.requestAnimationFrame(() => {
      const styles = getComputedStyle(document.documentElement)
      setPrimaryColor(rgbToHex(styles.getPropertyValue('--color-text-primary'), resolvedTheme === 'dark' ? '#FFFFFF' : '#0F172A'))
      setMutedColor(rgbToHex(styles.getPropertyValue('--color-text-muted'), resolvedTheme === 'dark' ? '#CBD5E1' : '#56657A'))
      setWorkspaceColor(DEFAULT_WORKSPACE_COLOUR[resolvedTheme])
      setWorkspaceShade(DEFAULT_WORKSPACE_SHADE)
    })
    showSaved()
  }, [setTheme, resolvedTheme, showSaved])

  if (!loaded) return null

  const modeLabel = resolvedTheme === 'dark' ? 'Night' : 'Day'
  const hasAnyColourCustom = (['light', 'dark'] as const).some(mode =>
    !!readModeTextColour(mode, 'primary')
    || !!readModeTextColour(mode, 'muted')
    || !!readModeWorkspaceColour(mode)
    || readModeWorkspaceShade(mode) !== null
  )

  const isDefault =
    textScale === DEFAULT_SCALE
    && headingScale === DEFAULT_SCALE
    && density === DEFAULT_DENSITY
    && !hasAnyColourCustom
    && theme === 'auto'

  return (
    <div className="mx-auto w-full max-w-3xl pb-8">
      <div className="mb-6 overflow-hidden rounded-3xl border border-accent/15 bg-gradient-to-br from-surface via-surface to-accent/10 p-5 shadow-[0_18px_50px_rgba(15,86,160,0.10)] sm:p-7 dark:from-[#0b203b] dark:via-[#0a192e] dark:to-[#08284b] dark:shadow-[0_22px_58px_rgba(0,0,0,0.28)]">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-accent/10 text-accent">
          <SlidersHorizontal className="h-5 w-5" />
        </div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">Personalise your workspace</p>
        <h1 className="text-2xl font-semibold leading-tight text-text-primary sm:text-3xl">Make OPS feel right for you.</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-muted">
          Choose your theme, tune readability and adjust the breathing room around dashboard panels.
          Every change previews instantly and is remembered on this device.
        </p>
      </div>

      <section className="mb-6" aria-labelledby="appearance-theme-heading">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <SunMoon className="h-4 w-4 text-accent" />
              <h2 id="appearance-theme-heading" className="text-sm font-semibold text-text-primary">Theme</h2>
            </div>
            <p className="mt-1 text-xs text-text-muted">
              Auto currently resolves to <span className="font-medium text-text-secondary">{resolvedTheme}</span>.
            </p>
          </div>
          <span className="text-[10px] text-text-muted">Light and night supported</span>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <ThemeChoice value="auto" active={theme === 'auto'} icon={Clock3} label="Auto" description="Day and night" onSelect={updateTheme} />
          <ThemeChoice value="light" active={theme === 'light'} icon={Sun} label="Light" description="Bright and clear" onSelect={updateTheme} />
          <ThemeChoice value="dark" active={theme === 'dark'} icon={Moon} label="Night" description="Calm and focused" onSelect={updateTheme} />
        </div>
        <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-surface/90 shadow-[0_12px_34px_rgba(15,86,160,0.08)] backdrop-blur-xl dark:bg-surface/85">
          <ColourControl
            label={`${modeLabel} workspace colour`}
            description={`Choose the ${modeLabel.toLowerCase()} hue on the upper spectrum, then use the lower shade slider to make that colour deeper or lighter. The OPS sidebar and top-bar backgrounds stay canonical.`}
            value={workspaceColor}
            onChange={updateWorkspaceColor}
            preview="theme"
            shade={workspaceShade}
            onShadeChange={updateWorkspaceShade}
          />
        </div>
        <p className="mt-2 text-[10px] leading-relaxed text-text-muted">Auto still switches at 06:00 / 18:00. The Night workspace remains dark. Theme colour changes the workspace, icons and accents; the sidebar and top-bar backgrounds keep the original OPS light/dark design.</p>
      </section>

      <section className="mb-6" aria-labelledby="appearance-readability-heading">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div className="flex items-center gap-2">
            <Type className="h-4 w-4 text-accent" />
            <h2 id="appearance-readability-heading" className="text-sm font-semibold text-text-primary">Readability</h2>
          </div>
          <span className="text-[10px] text-text-muted">Live across the dashboard</span>
        </div>

        <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-surface/90 shadow-[0_12px_34px_rgba(15,86,160,0.08)] backdrop-blur-xl dark:bg-surface/85 dark:shadow-[0_16px_40px_rgba(0,0,0,0.22)]">
          <ScaleControl
            icon={Type}
            label="Text size"
            description="Scales body text, labels and numbers throughout OPS."
            value={textScale}
            min={0.8}
            max={1.3}
            onChange={updateText}
          />
          <ScaleControl
            icon={Heading}
            label="Heading size"
            description="Adjusts heading emphasis independently from body text."
            value={headingScale}
            min={0.8}
            max={1.5}
            onChange={updateHeading}
          />
        </div>
      </section>

      <section className="mb-6" aria-labelledby="appearance-colour-heading">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div className="flex items-center gap-2"><Palette className="h-4 w-4 text-accent" /><h2 id="appearance-colour-heading" className="text-sm font-semibold text-text-primary">Text colours</h2></div>
          <span className="text-[10px] text-text-muted">Editing {modeLabel} mode</span>
        </div>
        <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-surface/90 shadow-[0_12px_34px_rgba(15,86,160,0.08)] backdrop-blur-xl dark:bg-surface/85">
          <ColourControl label={`${modeLabel} primary text`} description={resolvedTheme === 'dark' ? 'Night defaults to white. Change this only if you intentionally want a different Night text colour.' : 'Your Day heading/name colour. It will be restored automatically when OPS returns to Day mode.'} value={primaryColor} onChange={updatePrimaryColor} />
          <ColourControl label={`${modeLabel} secondary / grey text`} description={resolvedTheme === 'dark' ? 'Night defaults to light grey for contrast. Customise only if you want a different Night value.' : 'Your Day muted/secondary colour. Drag darker if grey labels feel too faint.'} value={mutedColor} onChange={updateMutedColor} />
        </div>
        <p className="mt-2 text-[10px] leading-relaxed text-text-muted">Day and Night text preferences are stored separately. If Night has never been customised, OPS automatically uses white primary text and light-grey secondary text. Use the colour swatch for an exact colour, or drag the spectrum line for quick adjustment.</p>
      </section>

      <section className="mb-6" aria-labelledby="appearance-spacing-heading">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div className="flex items-center gap-2">
            <Rows3 className="h-4 w-4 text-accent" />
            <h2 id="appearance-spacing-heading" className="text-sm font-semibold text-text-primary">Page spacing</h2>
          </div>
          <span className="text-[10px] text-text-muted">Panel breathing room</span>
        </div>

        <div className="grid grid-cols-3 gap-2 rounded-2xl border border-border bg-surface/90 p-2 shadow-[0_12px_34px_rgba(15,86,160,0.08)] backdrop-blur-xl dark:bg-surface/85 dark:shadow-[0_16px_40px_rgba(0,0,0,0.22)]">
          {(['compact', 'comfortable', 'spacious'] as Density[]).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={density === option}
              onClick={() => updateDensity(option)}
              className={cn(
                'min-h-11 rounded-xl px-2 text-xs font-medium capitalize transition-all cursor-pointer',
                density === option
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-text-muted hover:bg-surface-elevated hover:text-text-primary'
              )}
            >
              {option}
            </button>
          ))}
        </div>
      </section>

      <section
        className="rounded-2xl border border-border bg-surface/90 p-4 shadow-[0_12px_34px_rgba(15,86,160,0.08)] backdrop-blur-xl sm:p-5 dark:bg-surface/85 dark:shadow-[0_16px_40px_rgba(0,0,0,0.22)]"
        aria-labelledby="appearance-preview-heading"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" />
            <h2 id="appearance-preview-heading" className="text-sm font-semibold text-text-primary">Live preview</h2>
          </div>
          <span className="text-[10px] text-text-muted">Dashboard card</span>
        </div>

        <div className={cn(
          'rounded-2xl border border-border bg-surface-elevated transition-all',
          density === 'compact' ? 'p-3' : density === 'spacious' ? 'p-6' : 'p-4'
        )}>
          <div className="mb-3 h-1.5 w-12 rounded-full bg-gradient-to-r from-blue-700 to-cyan-400" />
          <h3 className="text-lg font-semibold text-text-primary">Your workspace, your way</h3>
          <p className="mt-1 text-sm leading-relaxed text-text-muted">
            See exactly how headings, labels, body text and page spacing will feel before leaving this page.
          </p>
        </div>
      </section>

      <div className="mt-5 flex min-h-10 items-center justify-between gap-3">
        <button
          type="button"
          onClick={reset}
          disabled={isDefault}
          className={cn(
            'flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-colors',
            isDefault
              ? 'cursor-not-allowed text-text-muted/40'
              : 'cursor-pointer text-text-muted hover:bg-surface-elevated hover:text-text-primary'
          )}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset defaults
        </button>

        <span
          aria-live="polite"
          className={cn(
            'flex items-center gap-1.5 text-xs text-success transition-opacity',
            saved ? 'opacity-100' : 'opacity-0'
          )}
        >
          <Check className="h-3.5 w-3.5" />
          Saved on this device
        </span>
      </div>
    </div>
  )
}
